import { useState } from "react";
import { ArrowRight, Calendar, Phone } from "lucide-react";
import { track } from "@/lib/analytics";
import { SalonPickerSheet } from "@/components/SalonPickerSheet";
import { withBookingUtm, REVIEW_COUNT_TEXT } from "@/lib/booking";
import type { Salon } from "@/data/types";

interface Props {
  salon?: Salon | null;
}

/**
 * Sticky mobile booking + call CTA. Brass background, fixed bottom.
 * - Booking button (flex-1): direct BokaDirekt link if a salon is in context,
 *   otherwise opens the salon picker sheet.
 * - Call button (compact): tel: link to the salon phone (shared central number).
 */
export function MobileBottomBar({ salon }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = salon ? `Boka tid på ${salon.shortName}` : "Boka tid";
  const sub = salon ? "Direkt till BokaDirekt →" : `4.8 ★ · ${REVIEW_COUNT_TEXT}`;
  const phoneHref = `tel:${salon?.phone ?? "+468371156"}`;

  const bookClass =
    "flex items-center justify-between gap-3 w-full px-5 py-4 bg-brass text-ink font-semibold rounded-none";

  const callClass =
    "flex shrink-0 flex-col items-center justify-center gap-0.5 self-stretch px-4 bg-ink/90 text-cream font-semibold";

  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-[70] flex bg-brass shadow-[0_-4px_20px_rgba(0,0,0,0.12)] rounded-none"
      role="region"
      aria-label="Boka tid eller ring"
    >
      {salon ? (
        <a
          href={withBookingUtm(salon.bokaDirektUrl)}
          target="_blank"
          rel="noopener noreferrer"
          data-cta="sticky_mobile"
          onClick={() =>
            track("book_now_click", {
              salon_name: salon.shortName,
              cta_location: "mobile_sticky_cta",
            })
          }
          className={bookClass}
        >
          <span className="flex items-center gap-2.5">
            <Calendar size={20} />
            <span className="flex flex-col leading-tight text-left">
              <span className="text-base">{label}</span>
              <span className="text-[11px] font-semibold text-ink">{sub}</span>
            </span>
          </span>
          <ArrowRight size={20} />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => {
            track("book_now_click", { cta_location: "mobile_sticky_cta" });
            setPickerOpen(true);
          }}
          className={bookClass}
        >
          <span className="flex items-center gap-2.5">
            <Calendar size={20} />
            <span className="flex flex-col leading-tight text-left">
              <span className="text-base">{label}</span>
              <span className="text-[11px] font-semibold text-ink">{sub}</span>
            </span>
          </span>
          <ArrowRight size={20} />
        </button>
      )}
      <a
        href={phoneHref}
        data-cta="sticky_mobile_call"
        aria-label={`Ring ${salon?.shortName ?? "Tailor's Barber"}`}
        onClick={() =>
          track("call_click", {
            salon_name: salon?.shortName,
            cta_location: "mobile_sticky_cta",
          })
        }
        className={callClass}
      >
        <Phone size={20} className="text-brass" />
        <span className="text-[11px] leading-none">Ring</span>
      </a>
      <SalonPickerSheet open={pickerOpen} action="book" onClose={() => setPickerOpen(false)} />
    </div>
  );
}
